// MCHD Room Scheduler - Building Form Component
// Form for creating and editing buildings

import React, { useEffect } from 'react';
import { Modal, TextInput, Textarea, Switch, Button, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';

interface Building {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
}

interface BuildingFormProps {
  building?: Building | null;
  onSubmit: (buildingData: any) => void;
  onClose: () => void;
}

/**
 * Building Form Component
 * Handles creating and editing buildings
 */
export const BuildingForm: React.FC<BuildingFormProps> = ({
  building,
  onSubmit,
  onClose
}) => {
  const isEditing = !!building;

  const form = useForm({
    initialValues: {
      name: '',
      description: '',
      isActive: true
    },
    validate: {
      name: (value) => {
        if (!value) return 'Building name is required';
        if (value.length < 2) return 'Building name must be at least 2 characters';
        if (value.length > 100) return 'Building name cannot exceed 100 characters';
        return null;
      },
      description: (value) => {
        if (value && value.length > 500) return 'Description cannot exceed 500 characters';
        return null;
      }
    }
  });

  /**
   * Initialize form with building data when editing
   */
  useEffect(() => {
    if (building) {
      form.setValues({
        name: building.name,
        description: building.description || '',
        isActive: building.isActive
      });
    }
  }, [building]);

  /**
   * Handle form submission
   */
  const handleSubmit = (values: typeof form.values) => {
    const submitData = {
      ...values,
      description: values.description || null
    };
    
    onSubmit(submitData);
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    form.reset();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={isEditing ? 'Edit Building' : 'Create New Building'}
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Building Name Field */}
          <TextInput
            label="Building Name"
            placeholder="Enter building name"
            required
            {...form.getInputProps('name')}
          />

          {/* Description Field */}
          <Textarea
            label="Description"
            placeholder="Enter building description (optional)"
            minRows={3}
            maxRows={6}
            {...form.getInputProps('description')}
          />

          {/* Status Field (only for editing) */}
          {isEditing && (
            <Switch
              label="Active Status"
              description="Whether this building is active and available for use"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />
          )}

          {/* Form Actions */}
          <Group justify="flex-end" mt="xl">
            <Button
              variant="outline"
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={form.values.name === ''} // Simple loading state
            >
              {isEditing ? 'Update Building' : 'Create Building'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
